import { type FC, type ReactNode, useLayoutEffect } from 'react';
import { NavigationProvider, type NavigationContextType } from './NavigationContext';
import { ServiceProvider } from './ServiceContext';
import { type ServiceConfig } from './ServiceRegistry';
import { federationConfigLoader } from '@/lib/moduleFederation/config/federationConfigLoader';
import {
  isAgaComponentsV2Selection,
  publishFederatedTheme,
  type FederatedThemeOverrides,
} from '@/lib/moduleFederation/runtime/federatedThemeBridge';

interface AppProviderProps {
  children: ReactNode;
  additionalServices?: ServiceConfig[];
  includeDefaultServices?: {
    dealcard?: boolean;
    funding?: boolean;
    collateral?: boolean;
    titan?: boolean;
  };
  navigation?: Partial<NavigationContextType>;
  // Legacy props for backward compatibility - prefer using navigation prop
  onNavigate?: (path: string) => void;
  onGoBack?: () => void;
  onReplace?: (path: string) => void;
  onPrefetch?: (path: string) => void;
  pathname?: string;
  query?: Record<string, any>;
  asPath?: string;
  /** MUI theme overrides for aga-components v2. The remote builds the theme. */
  theme?: FederatedThemeOverrides;
}

/**
 * AppProvider that combines ServiceProvider and NavigationProvider
 * This provides a single point of configuration for the entire application
 *
 * Navigation can be provided as a single object or as individual props (legacy).
 * Prefer using the `navigation` prop for cleaner API.
 */
export const AppProvider: FC<AppProviderProps> = ({
  children,
  additionalServices,
  includeDefaultServices,
  navigation,
  // Legacy props
  onNavigate,
  onGoBack,
  onReplace,
  onPrefetch,
  pathname,
  query,
  asPath,
  theme,
}) => {
  useLayoutEffect(() => {
    let cancelled = false;
    void federationConfigLoader.loadConfig().then(() => {
      if (cancelled) {
        return;
      }
      const remote = federationConfigLoader.getRemoteConfig('aga-components');
      const version = remote?.version;
      const baseUrl = version ? remote?.versions?.[version] : remote?.url;
      if (!isAgaComponentsV2Selection(version, baseUrl)) {
        return;
      }
      publishFederatedTheme(theme);
    });
    return () => {
      cancelled = true;
    };
  }, [theme]);

  // Use navigation object if provided, otherwise fall back to individual props
  const navProps = navigation
    ? {
        navigation,
      }
    : {
        onNavigate,
        onGoBack,
        onReplace,
        onPrefetch,
        pathname,
        query,
        asPath,
      };

  return (
    <NavigationProvider {...navProps}>
      <ServiceProvider additionalServices={additionalServices} includeDefaults={includeDefaultServices}>
        {children}
      </ServiceProvider>
    </NavigationProvider>
  );
};
